import React, {
  useCallback,
  useRef,
  useEffect,
  useState,
} from 'react';
import classNames from 'classnames';
import AsyncCreatableSelect from 'react-select/async-creatable';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import { debounce } from 'lodash';
import { CirclePlus } from '@saleshandy/icons';
import {
  formatIncompletePhoneNumber,
  parsePhoneNumber,
} from 'libphonenumber-js';
import {
  countryCodePhoneInputPickerData,
  countryByISOCodeMap,
} from '../country-code-phone-input-picker/helper';
import { getCountryFlagImageUrl } from '../country-code-phone-input-picker/country-flag-image';
import './phone-number-search-input-v2.scss';

export interface TeamMember {
  id: number;
  firstName: string;
  lastName: string;
}

export interface PhoneNumberOption {
  __isNew__?: boolean;
  value: string;
  label: string;
  phoneNumber: string;
  teamMember: TeamMember | null;
  id: number;
}

interface CountryEntry {
  name: string;
  code: string;
  iso: string;
  flag: string;
}

interface PhoneNumberSearchInputV2Props {
  className: string;
  loadOptions: (search: string) => Promise<PhoneNumberOption[]>;
  disabled?: boolean;
  value?: PhoneNumberOption | null;
  onChange?: (option: PhoneNumberOption | null) => void;
  isCreatable?: boolean;
  errorMessage?: string;
  cacheRef?: React.MutableRefObject<PhoneNumberOption[] | null>;
  formatOptionLabel?: (option: PhoneNumberOption) => React.ReactNode;
  placeholder?: string;
  formatCountryCode?: string;
}

// ---------------------------------------------------------------------------
// Module-level custom react-select components (stable refs — no re-renders)
// ---------------------------------------------------------------------------

const CustomControl = ({ children, ...props }: any) => {
  const { selectedCountry, onCountryButtonClick } = props.selectProps;
  return (
    <components.Control {...props}>
      <button
        type="button"
        className="bs-phone-number-v2__country-btn"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onCountryButtonClick();
        }}
        tabIndex={-1}
      >
        <span
          className="bs-phone-number-v2__flag"
          role="img"
          aria-label={`${selectedCountry?.name ?? ''} ${selectedCountry?.code ?? ''}`}
        >
          <img
            src={getCountryFlagImageUrl(selectedCountry?.iso ?? '')}
            alt=""
            width={16}
            height={12}
            className="bs-phone-number-v2__flag-img bs-phone-number-v2__flag-img--control"
            loading="lazy"
            decoding="async"
          />
        </span>
        <span className="bs-phone-number-v2__code">{selectedCountry?.code}</span>
        <i className="sh-chevron-down bs-phone-number-v2__chevron" />
      </button>
      <div className="bs-phone-number-v2__divider" />
      {children}
    </components.Control>
  );
};

const CustomSingleValue = ({ children, data, ...props }: any) => {
  const { selectedCountry } = props.selectProps;
  const rawNumber: string = data?.phoneNumber || data?.value || '';
  const code: string = selectedCountry?.code || '';
  const stripped = rawNumber.startsWith(code)
    ? rawNumber.slice(code.length).trimStart()
    : rawNumber;
  return (
    <components.SingleValue {...props} data={data}>
      {stripped || children}
    </components.SingleValue>
  );
};

const ClearIndicator = (props: any) => {
  const {
    getStyles,
    innerProps: { ref, ...restInnerProps },
  } = props;
  return (
    <div
      {...restInnerProps}
      ref={ref}
      style={getStyles('clearIndicator', props)}
    >
      <i className="sh-close-o cursor-pointer clear-indicator" />
    </div>
  );
};

// ---------------------------------------------------------------------------

const US_COUNTRY = countryByISOCodeMap.get('US') as CountryEntry;

const PhoneNumberSearchInputV2: React.FC<PhoneNumberSearchInputV2Props> = ({
  className,
  loadOptions,
  disabled,
  value,
  onChange,
  isCreatable = true,
  errorMessage,
  cacheRef,
  formatOptionLabel: formatOptionLabelProp,
  placeholder = 'Enter phone number',
  formatCountryCode = 'US',
}) => {
  const [localValue, setLocalValue] = useState<PhoneNumberOption | null>(value || null);
  const [selectedCountry, setSelectedCountry] = useState<CountryEntry>(US_COUNTRY);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [countryFilter, setCountryFilter] = useState('');
  const [defaultOptions, setDefaultOptions] = useState<PhoneNumberOption[]>([]);
  const [isDefaultLoading, setIsDefaultLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const pickerRef = useRef<HTMLDivElement>(null);
  const internalCacheRef = useRef<PhoneNumberOption[] | null>(null);
  const activeCacheRef = cacheRef ?? internalCacheRef;

  // Sync controlled value
  useEffect(() => {
    const incoming = value || null;
    setLocalValue(incoming);
    if (incoming?.phoneNumber) {
      try {
        const parsed = parsePhoneNumber(incoming.phoneNumber);
        if (parsed?.country) {
          const country = countryByISOCodeMap.get(parsed.country);
          if (country) setSelectedCountry(country as CountryEntry);
        }
      } catch {
        // ignore parse errors
      }
    }
  }, [value]);

  // Close picker when clicking outside
  useEffect(() => {
    if (!showCountryPicker) return undefined;
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowCountryPicker(false);
        setCountryFilter('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCountryPicker]);

  const handleCountrySelect = useCallback((country: CountryEntry) => {
    setSelectedCountry(country);
    setShowCountryPicker(false);
    setCountryFilter('');
  }, []);

  const handleInputChange = useCallback(
    (newValue: string, actionMeta: { action: string }) => {
      if (actionMeta.action === 'input-change') {
        setInputValue(newValue);
      } else {
        setInputValue('');
      }
    },
    [],
  );

  const handleChange = useCallback(
    (option: PhoneNumberOption | null) => {
      setLocalValue(option);
      setInputValue('');
      if (option?.phoneNumber) {
        try {
          const parsed = parsePhoneNumber(option.phoneNumber);
          if (parsed?.country) {
            const country = countryByISOCodeMap.get(parsed.country);
            if (country) setSelectedCountry(country as CountryEntry);
          }
        } catch {
          // ignore
        }
      }
      if (onChange) onChange(option);
    },
    [onChange],
  );

  const defaultFormatOptionLabel = useCallback(
    (option: PhoneNumberOption) => {
      // eslint-disable-next-line no-underscore-dangle
      if (option?.__isNew__) {
        return (
          <div className="inline-dropdown-add-new-column-container">
            <CirclePlus color="#1D4ED8" />
            <span className="inline-dropdown-add-column">Add "{option.value}"</span>
          </div>
        );
      }
      if (option.teamMember) {
        const fullName = `${option.teamMember.firstName} ${option.teamMember.lastName}`;
        return (
          <div className="smart-search-option">
            <span className="smart-search-option-name">{fullName}</span>
            <span className="phone-number-text">
              &nbsp;&nbsp;
              {option.phoneNumber ? formatIncompletePhoneNumber(option.phoneNumber, formatCountryCode as any) : ''}
            </span>
          </div>
        );
      }
      return (
        <span>
          {(option.phoneNumber ? formatIncompletePhoneNumber(option.phoneNumber, formatCountryCode as any) : '') || option.value}
        </span>
      );
    },
    [formatCountryCode],
  );

  const resolvedFormatOptionLabel = formatOptionLabelProp ?? defaultFormatOptionLabel;

  // Debounced load options
  const debouncedLoadOptionsRef = useRef<ReturnType<typeof debounce>>();

  const debouncedLoadOptions = useCallback(
    (inputValue: string): Promise<PhoneNumberOption[]> =>
      new Promise((resolve) => {
        if (debouncedLoadOptionsRef.current) {
          debouncedLoadOptionsRef.current.cancel();
        }
        debouncedLoadOptionsRef.current = debounce(() => {
          loadOptions(inputValue).then(resolve);
        }, 500);
        debouncedLoadOptionsRef.current();
      }),
    [loadOptions],
  );

  useEffect(
    () => () => {
      if (debouncedLoadOptionsRef.current) {
        debouncedLoadOptionsRef.current.cancel();
      }
    },
    [],
  );

  const handleMenuOpen = useCallback(async () => {
    if (localValue) {
      const rawNum = localValue.phoneNumber || localValue.value || '';
      const prefix = selectedCountry.code;
      const stripped = rawNum.startsWith(prefix)
        ? rawNum.slice(prefix.length).trimStart()
        : rawNum;
      setInputValue(stripped);
    }

    if (activeCacheRef.current !== null) {
      setDefaultOptions(activeCacheRef.current);
    } else {
      setIsDefaultLoading(true);
      try {
        const options = await loadOptions('');
        activeCacheRef.current = options;
        setDefaultOptions(options);
      } finally {
        setIsDefaultLoading(false);
      }
    }
  }, [loadOptions, activeCacheRef, localValue, selectedCountry]);

  const isValidNewOption = useCallback((inputValue: string) => {
    const trimmed = inputValue.trim();
    if (!trimmed) return false;
    // Allow digits-only (will prepend calling code) or full E.164 number
    return /^\+?\d+$/.test(trimmed);
  }, []);

  const handleCreateOption = useCallback(
    (inputValue: string) => {
      const trimmed = inputValue.trim();
      // Prepend selected country code if user typed only digits
      const fullNumber = trimmed.startsWith('+')
        ? trimmed
        : `${selectedCountry.code}${trimmed}`;

      const newOption: PhoneNumberOption = {
        value: fullNumber,
        label: fullNumber,
        phoneNumber: fullNumber,
        teamMember: null,
        id: 0,
      };

      setLocalValue(newOption);
      if (onChange) onChange(newOption);
    },
    [onChange, selectedCountry],
  );

  const filteredCountries = countryFilter
    ? countryCodePhoneInputPickerData.filter(
        (c) =>
          c.name.toLowerCase().includes(countryFilter.toLowerCase()) ||
          c.code.includes(countryFilter),
      )
    : countryCodePhoneInputPickerData;

  const commonSelectProps = {
    cacheOptions: true,
    loadOptions: debouncedLoadOptions,
    defaultOptions,
    value: localValue,
    inputValue,
    onInputChange: handleInputChange,
    onChange: (option: PhoneNumberOption | null) => handleChange(option),
    onMenuOpen: handleMenuOpen,
    formatOptionLabel: resolvedFormatOptionLabel,
    placeholder,
    isDisabled: disabled,
    isLoading: isDefaultLoading,
    className: 'react-select-container',
    classNamePrefix: 'react-select',
    isClearable: true,
    noOptionsMessage: ({ inputValue: searchInput }: { inputValue: string }) =>
      searchInput ? 'No members found' : null,
    loadingMessage: () => 'Loading...',
    components: {
      ClearIndicator,
      Control: CustomControl,
      SingleValue: CustomSingleValue,
    },
    // Pass state/handlers via selectProps to the custom components
    selectedCountry,
    onCountryButtonClick: () => setShowCountryPicker((prev) => !prev),
  };

  return (
    <div
      className={classNames('bs-phone-number-v2', className, {
        'bs-phone-number-v2--error': Boolean(errorMessage),
        'bs-phone-number-v2--disabled': disabled,
      })}
    >
      <div className="bs-phone-number-v2__input-wrapper">
        {isCreatable ? (
          <AsyncCreatableSelect<PhoneNumberOption, false>
            {...commonSelectProps}
            onCreateOption={handleCreateOption}
            isValidNewOption={isValidNewOption}
          />
        ) : (
          <AsyncSelect<PhoneNumberOption, false> {...commonSelectProps} />
        )}

        {showCountryPicker && (
          <div className="bs-phone-number-v2__country-picker" ref={pickerRef}>
            <div className="bs-phone-number-v2__country-search">
              <input
                type="text"
                className="bs-phone-number-v2__country-search-input"
                placeholder="Search country..."
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
            </div>
            <ul className="bs-phone-number-v2__country-list">
              {filteredCountries.map((country) => (
                <li
                  key={country.iso}
                  className={classNames('bs-phone-number-v2__country-item', {
                    'bs-phone-number-v2__country-item--selected':
                      country.iso === selectedCountry.iso,
                  })}
                  onMouseDown={() => handleCountrySelect(country)}
                >
                  <span
                    className="bs-phone-number-v2__country-flag"
                    role="img"
                    aria-label={`${country.name} ${country.code}`}
                  >
                    <img
                      src={getCountryFlagImageUrl(country.iso)}
                      alt=""
                      width={20}
                      height={15}
                      className="bs-phone-number-v2__flag-img bs-phone-number-v2__flag-img--list"
                      loading="lazy"
                      decoding="async"
                    />
                  </span>
                  <span className="bs-phone-number-v2__country-name">{country.name}</span>
                  <span className="bs-phone-number-v2__country-code">{country.code}</span>
                </li>
              ))}
              {filteredCountries.length === 0 && (
                <li className="bs-phone-number-v2__country-empty">No results</li>
              )}
            </ul>
          </div>
        )}
      </div>

      {errorMessage ? (
        <p className="regular-1 red-txt-6 bs-phone-number-v2__error" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
};

export default PhoneNumberSearchInputV2;
